import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import axios from 'axios';
import { AuthContext } from '../../components/AuthContext';
import Swal from 'sweetalert2';
import { 
  FaPen, FaTrash, FaPlus, FaArrowUp, FaArrowDown, 
  FaArrowRight, FaArrowLeft, FaSearch, FaKeyboard,
  FaCheckSquare, FaSquare, FaChevronUp, FaChevronDown
} from 'react-icons/fa';

const RoleManagement = () => {
  // Existing state
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { user } = useContext(AuthContext);
  
  // Dashboard features state
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  
  // Selected item tracking
  const [selectedAvailableItems, setSelectedAvailableItems] = useState([]);
  const [selectedSelectedItems, setSelectedSelectedItems] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    postName: '',
    dashboardAccess: []
  });
  
  // New state for search/filtering
  const [availableSearch, setAvailableSearch] = useState('');
  const [selectedSearch, setSelectedSearch] = useState('');
  
  // State to track which list is focused
  const [focusedList, setFocusedList] = useState('available'); // 'available' or 'selected'
  
  // Refs for the list containers
  const availableListRef = useRef(null);
  const selectedListRef = useRef(null);
  const availableSearchRef = useRef(null);
  const selectedSearchRef = useRef(null);
  
  // Show/hide keyboard shortcuts guide
  const [showKeyboardGuide, setShowKeyboardGuide] = useState(false);
  
  // Group features by category (if they follow a pattern like "category-feature")
  const [groupByCategory, setGroupByCategory] = useState(false);

  // Fetch roles and company domains on mount
  useEffect(() => {
    fetchRoles();
    fetchCompanyDomains();
  }, []);
  
  // Filter features based on search terms
  const filteredAvailableFeatures = availableFeatures.filter(item => 
    item.content.toLowerCase().includes(availableSearch.toLowerCase())
  );
  
  const filteredSelectedFeatures = selectedFeatures.filter(item => 
    item.content.toLowerCase().includes(selectedSearch.toLowerCase())
  );
  
  // Group features by category if enabled
  const groupedAvailableFeatures = useMemo(() => {
    if (!groupByCategory) return { uncategorized: filteredAvailableFeatures };
    
    return filteredAvailableFeatures.reduce((groups, feature) => {
      // Try to extract category from feature ID (assuming format like "category-feature")
      let category = "uncategorized";
      if (feature.id.includes('-')) {
        category = feature.id.split('-')[0];
      }
      
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(feature);
      return groups;
    }, {});
  }, [filteredAvailableFeatures, groupByCategory]);
  
  const groupedSelectedFeatures = useMemo(() => {
    if (!groupByCategory) return { uncategorized: filteredSelectedFeatures };
    
    return filteredSelectedFeatures.reduce((groups, feature) => {
      // Try to extract category from feature ID (assuming format like "category-feature")
      let category = "uncategorized";
      if (feature.id.includes('-')) {
        category = feature.id.split('-')[0];
      }
      
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(feature);
      return groups;
    }, {});
  }, [filteredSelectedFeatures, groupByCategory]);
  
  // Track which category is expanded
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Fetch roles and company domains on mount
  useEffect(() => {
    fetchRoles();
    fetchCompanyDomains();
  }, []);

  // Improved fetchCompanyDomains function with better error handling
  const fetchCompanyDomains = async () => {
    try {
      const token = localStorage.getItem('token');
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Make sure we have a valid company ID
      if (!user?.user?.company) {
        console.error('Company ID not available');
        setAvailableFeatures([]);
        setSelectedFeatures([{ id: 'dashboard', content: 'Dashboard' }]);
        return;
      }

      // Log the company ID we're trying to fetch
      console.log('Fetching company with ID:', user.user.company);
      
      // Fetch company details to get domains
      const response = await api.get(`/companies/${user.user.company._id || user.user.company}`);
      
      if (!response.data) {
        console.error('No company data returned');
        return;
      }
      
      const company = response.data;
      console.log('Company domains:', company.domains);
      
      // Extract unique employee dashboard access features from all domains
      const allFeatures = new Set(['dashboard']); // Always include dashboard
      
      if (company.domains && Array.isArray(company.domains) && company.domains.length > 0) {
        company.domains.forEach(domain => {
          if (domain.employeeDashboardAccess && Array.isArray(domain.employeeDashboardAccess)) {
            domain.employeeDashboardAccess.forEach(feature => {
              if (feature) allFeatures.add(feature);
            });
          }
        });
      }
      
      // Convert to array of objects for rendering
      const features = Array.from(allFeatures).map(feature => ({
        id: feature,
        content: feature.replace(/-/g, ' ')
      }));
      
      console.log('Available dashboard features:', features);
      setAvailableFeatures(features.filter(f => f.id !== 'dashboard'));
      setSelectedFeatures(features.filter(f => f.id === 'dashboard')); // Dashboard is always selected
    } catch (error) {
      console.error('Error fetching company domains:', error);
      // Set dashboard as fallback
      setAvailableFeatures([]);
      setSelectedFeatures([{ id: 'dashboard', content: 'Dashboard' }]);
      Swal.fire('Error', 'Failed to load available dashboard features', 'error');
    }
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('Fetching roles for company:', user.user.company);
      // Fetch roles by company
      const response = await api.get(`/roles/company/${user.user.company._id || user.user.company}`);
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      Swal.fire('Error', 'Failed to fetch roles', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle selecting items in the available list
  const handleSelectAvailable = (itemId) => {
    if (selectedAvailableItems.includes(itemId)) {
      setSelectedAvailableItems(selectedAvailableItems.filter(id => id !== itemId));
    } else {
      setSelectedAvailableItems([...selectedAvailableItems, itemId]);
    }
  };

  // Handle selecting items in the selected list
  const handleSelectSelected = (itemId) => {
    if (itemId === 'dashboard') return; // Don't allow selecting dashboard
    
    if (selectedSelectedItems.includes(itemId)) {
      setSelectedSelectedItems(selectedSelectedItems.filter(id => id !== itemId));
    } else {
      setSelectedSelectedItems([...selectedSelectedItems, itemId]);
    }
  };

  // Handle double-click to move items quickly
  const handleDoubleClickAvailable = (itemId) => {
    setSelectedAvailableItems([itemId]);
    setTimeout(() => {
      moveToSelected();
    }, 100);
  };

  const handleDoubleClickSelected = (itemId) => {
    if (itemId === 'dashboard') return; // Don't allow removing dashboard
    setSelectedSelectedItems([itemId]);
    setTimeout(() => {
      moveToAvailable();
    }, 100);
  };

  // Move selected items from available to selected
  const moveToSelected = () => {
    if (selectedAvailableItems.length === 0) return;
    
    const itemsToMove = availableFeatures.filter(item => selectedAvailableItems.includes(item.id));
    
    setSelectedFeatures([...selectedFeatures, ...itemsToMove]);
    setAvailableFeatures(availableFeatures.filter(item => !selectedAvailableItems.includes(item.id)));
    
    // Update form data
    setFormData({
      ...formData,
      dashboardAccess: [...selectedFeatures, ...itemsToMove].map(item => item.id)
    });
    
    // Clear selection
    setSelectedAvailableItems([]);
  };

  // Move selected items from selected to available
  const moveToAvailable = () => {
    if (selectedSelectedItems.length === 0) return;
    
    const itemsToMove = selectedFeatures.filter(item => selectedSelectedItems.includes(item.id));
    
    setAvailableFeatures([...availableFeatures, ...itemsToMove]);
    setSelectedFeatures(selectedFeatures.filter(item => !selectedSelectedItems.includes(item.id)));
    
    // Update form data
    setFormData({
      ...formData,
      dashboardAccess: selectedFeatures.filter(item => !selectedSelectedItems.includes(item.id)).map(item => item.id)
    });
    
    // Clear selection
    setSelectedSelectedItems([]);
  };

  // Move selected items up in the selected list
  const moveUp = () => {
    if (selectedSelectedItems.length === 0) return;
    
    // Don't allow moving dashboard
    const itemsToMove = selectedSelectedItems.filter(id => id !== 'dashboard');
    if (itemsToMove.length === 0) return;
    
    // Create new array
    const newSelectedFeatures = [...selectedFeatures];
    
    // For each item to move, find its index and try to move it up
    itemsToMove.forEach(itemId => {
      const currentIndex = newSelectedFeatures.findIndex(item => item.id === itemId);
      if (currentIndex > 0) {
        // Check if the target position is not occupied by another item we're moving
        const targetIndex = currentIndex - 1;
        const targetItem = newSelectedFeatures[targetIndex];
        
        // If target position has an item we're not moving, swap
        if (!itemsToMove.includes(targetItem.id)) {
          // Swap items
          newSelectedFeatures[targetIndex] = newSelectedFeatures[currentIndex];
          newSelectedFeatures[currentIndex] = targetItem;
        }
      }
    });
    
    setSelectedFeatures(newSelectedFeatures);
    
    // Update form data
    setFormData({
      ...formData,
      dashboardAccess: newSelectedFeatures.map(item => item.id)
    });
  };

  // Move selected items down in the selected list
  const moveDown = () => {
    if (selectedSelectedItems.length === 0) return;
    
    // Don't allow moving dashboard
    const itemsToMove = selectedSelectedItems.filter(id => id !== 'dashboard');
    if (itemsToMove.length === 0) return;
    
    // Create new array
    const newSelectedFeatures = [...selectedFeatures];
    
    // Process items in reverse order to avoid conflicts
    const reversedItems = [...itemsToMove].reverse();
    
    // For each item to move, find its index and try to move it down
    reversedItems.forEach(itemId => {
      const currentIndex = newSelectedFeatures.findIndex(item => item.id === itemId);
      if (currentIndex < newSelectedFeatures.length - 1 && currentIndex !== -1) {
        // Check if the target position is not occupied by another item we're moving
        const targetIndex = currentIndex + 1;
        const targetItem = newSelectedFeatures[targetIndex];
        
        // If target position has an item we're not moving, swap
        if (!itemsToMove.includes(targetItem.id)) {
          // Swap items
          newSelectedFeatures[targetIndex] = newSelectedFeatures[currentIndex];
          newSelectedFeatures[currentIndex] = targetItem;
        }
      }
    });
    
    setSelectedFeatures(newSelectedFeatures);
    
    // Update form data
    setFormData({
      ...formData,
      dashboardAccess: newSelectedFeatures.map(item => item.id)
    });
  };

  const resetForm = () => {
    setFormData({
      postName: '',
      dashboardAccess: []
    });
    setCurrentRole(null);
    setIsEditing(false);
    setSelectedAvailableItems([]);
    setSelectedSelectedItems([]);
  };

  const openModal = (role = null) => {
    if (role) {
      setFormData({
        postName: role.postName,
        dashboardAccess: role.dashboardAccess || []
      });
      setCurrentRole(role);
      setIsEditing(true);
      
      // Set up lists based on role's dashboardAccess
      const roleAccess = role.dashboardAccess || [];
      
      // Get all features and split them based on role access
      const allFeatures = [...availableFeatures, ...selectedFeatures]
        .filter((item, index, self) => 
          index === self.findIndex(t => t.id === item.id)
        );
      
      const selected = allFeatures.filter(f => roleAccess.includes(f.id));
      const available = allFeatures.filter(f => !roleAccess.includes(f.id));
      
      setSelectedFeatures(selected);
      setAvailableFeatures(available);
    } else {
      resetForm();
      // For new roles, ensure dashboard is selected
      setFormData(prev => ({
        ...prev,
        dashboardAccess: ['dashboard']
      }));
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.postName.trim()) {
      Swal.fire('Error', 'Role name is required', 'error');
      return;
    }

    // Always include dashboard access
    if (!formData.dashboardAccess.includes('dashboard')) {
      formData.dashboardAccess.push('dashboard');
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Add company information to the role
      const roleData = {
        ...formData,
        company: (user.user.company._id || user.user.company)
      };

      if (isEditing && currentRole) {
        // Update existing role
        await api.put(`/roles/${currentRole._id}`, roleData);
        Swal.fire('Success', 'Role updated successfully', 'success');
      } else {
        // Create new role
        await api.post('/roles', roleData);
        Swal.fire('Success', 'Role created successfully', 'success');
      }

      closeModal();
      fetchRoles();
    } catch (error) {
      console.error('Error saving role:', error);
      const errorMsg = error.response?.data?.error || 'Failed to save role';
      Swal.fire('Error', errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      // Check if role is in use before deleting
      const token = localStorage.getItem('token');
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Change this line to use the correct endpoint
      const response = await api.get(`/roles/employees/${roleId}`);
      
      if (response.data.length > 0) {
        Swal.fire(
          'Cannot Delete',
          `This role is assigned to ${response.data.length} employee(s). Please reassign them before deleting.`,
          'warning'
        );
        return;
      }

      // Ask for confirmation before deletion
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'You will not be able to recover this role!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, keep it'
      });

      if (result.isConfirmed) {
        await api.delete(`/roles/${roleId}`);
        Swal.fire('Deleted!', 'Role has been deleted.', 'success');
        fetchRoles();
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      Swal.fire('Error', 'Failed to delete role', 'error');
    }
  };

  // Select all items in available list
  const selectAllAvailable = () => {
    setSelectedAvailableItems(filteredAvailableFeatures.map(item => item.id));
  };
  
  // Deselect all items in available list
  const deselectAllAvailable = () => {
    setSelectedAvailableItems([]);
  };
  
  // Select all items in selected list
  const selectAllSelected = () => {
    setSelectedSelectedItems(
      filteredSelectedFeatures
        .filter(item => item.id !== 'dashboard')
        .map(item => item.id)
    );
  };
  
  // Deselect all items in selected list
  const deselectAllSelected = () => {
    setSelectedSelectedItems([]);
  };
  
  // Move all filtered available items to selected
  const moveAllToSelected = () => {
    const itemsToMove = filteredAvailableFeatures;
    
    setSelectedFeatures([...selectedFeatures, ...itemsToMove]);
    setAvailableFeatures(availableFeatures.filter(item => 
      !itemsToMove.some(moveItem => moveItem.id === item.id)
    ));
    
    // Update form data
    setFormData({
      ...formData,
      dashboardAccess: [...selectedFeatures, ...itemsToMove].map(item => item.id)
    });
    
    // Clear selection
    setSelectedAvailableItems([]);
  };
  
  // Move all filtered selected items to available (except dashboard)
  const moveAllToAvailable = () => {
    const itemsToMove = filteredSelectedFeatures.filter(item => item.id !== 'dashboard');
    
    setAvailableFeatures([...availableFeatures, ...itemsToMove]);
    setSelectedFeatures(selectedFeatures.filter(item => 
      item.id === 'dashboard' || !itemsToMove.some(moveItem => moveItem.id === item.id)
    ));
    
    // Update form data
    setFormData({
      ...formData,
      dashboardAccess: selectedFeatures
        .filter(item => item.id === 'dashboard' || !itemsToMove.some(moveItem => moveItem.id === item.id))
        .map(item => item.id)
    });
    
    // Clear selection
    setSelectedSelectedItems([]);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, listType) => {
    // If user is typing in search field, don't handle navigation keys
    if (e.target.tagName === 'INPUT') return;
    
    // Set focus to the current list
    setFocusedList(listType);
    
    const isAvailableList = listType === 'available';
    const items = isAvailableList ? filteredAvailableFeatures : filteredSelectedFeatures;
    const selectedItems = isAvailableList ? selectedAvailableItems : selectedSelectedItems;
    const setSelectedItems = isAvailableList ? setSelectedAvailableItems : setSelectedSelectedItems;
    
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        // If no items are selected, select the last item
        if (selectedItems.length === 0 && items.length > 0) {
          setSelectedItems([items[items.length - 1].id]);
        } else if (selectedItems.length === 1) {
          // Find the current selected item index
          const currentIndex = items.findIndex(item => item.id === selectedItems[0]);
          if (currentIndex > 0) {
            // Select the previous item
            setSelectedItems([items[currentIndex - 1].id]);
          }
        }
        break;
        
      case 'ArrowDown':
        e.preventDefault();
        // If no items are selected, select the first item
        if (selectedItems.length === 0 && items.length > 0) {
          setSelectedItems([items[0].id]);
        } else if (selectedItems.length === 1) {
          // Find the current selected item index
          const currentIndex = items.findIndex(item => item.id === selectedItems[0]);
          if (currentIndex < items.length - 1) {
            // Select the next item
            setSelectedItems([items[currentIndex + 1].id]);
          }
        }
        break;
        
      case 'Home':
        e.preventDefault();
        if (items.length > 0) {
          setSelectedItems([items[0].id]);
        }
        break;
        
      case 'End':
        e.preventDefault();
        if (items.length > 0) {
          setSelectedItems([items[items.length - 1].id]);
        }
        break;
        
      case 'PageUp':
        e.preventDefault();
        if (selectedItems.length === 1 && items.length > 0) {
          const currentIndex = items.findIndex(item => item.id === selectedItems[0]);
          const newIndex = Math.max(0, currentIndex - 5);
          setSelectedItems([items[newIndex].id]);
        }
        break;
        
      case 'PageDown':
        e.preventDefault();
        if (selectedItems.length === 1 && items.length > 0) {
          const currentIndex = items.findIndex(item => item.id === selectedItems[0]);
          const newIndex = Math.min(items.length - 1, currentIndex + 5);
          setSelectedItems([items[newIndex].id]);
        }
        break;
        
      case ' ': // Space
        e.preventDefault();
        // Toggle selection of the focused item
        if (e.ctrlKey && selectedItems.length === 1) {
          const itemId = selectedItems[0];
          if (selectedItems.includes(itemId)) {
            setSelectedItems(selectedItems.filter(id => id !== itemId));
          } else if (itemId !== 'dashboard' || isAvailableList) {
            setSelectedItems([...selectedItems, itemId]);
          }
        }
        break;
        
      case 'a':
      case 'A':
        if (e.ctrlKey) {
          e.preventDefault();
          if (isAvailableList) {
            selectAllAvailable();
          } else {
            selectAllSelected();
          }
        }
        break;
        
      case 'Enter':
        e.preventDefault();
        if (isAvailableList && selectedItems.length > 0) {
          moveToSelected();
        } else if (!isAvailableList && selectedItems.length > 0) {
          moveToAvailable();
        }
        break;
        
      case 'Tab':
        // Don't prevent default to allow normal tab navigation
        // But set the focused list appropriately
        if (!e.shiftKey) {
          if (isAvailableList) {
            setFocusedList('selected');
          }
        } else {
          if (!isAvailableList) {
            setFocusedList('available');
          }
        }
        break;
        
      case '/':
        e.preventDefault();
        // Focus on the search input for the current list
        if (isAvailableList && availableSearchRef.current) {
          availableSearchRef.current.focus();
        } else if (!isAvailableList && selectedSearchRef.current) {
          selectedSearchRef.current.focus();
        }
        break;
        
      case 'Escape':
        // Clear selections
        if (isAvailableList) {
          deselectAllAvailable();
          setAvailableSearch('');
        } else {
          deselectAllSelected();
          setSelectedSearch('');
        }
        break;
        
      default:
        break;
    }
  };
  
  // Focus the appropriate list when the modal opens
  useEffect(() => {
    if (showModal) {
      if (availableListRef.current && focusedList === 'available') {
        availableListRef.current.focus();
      } else if (selectedListRef.current && focusedList === 'selected') {
        selectedListRef.current.focus();
      }
    }
  }, [showModal, focusedList]);
  
  // Initialize expanded categories when features load
  useEffect(() => {
    if (groupByCategory) {
      const categories = {};
      
      // Get all category names
      [...availableFeatures, ...selectedFeatures].forEach(feature => {
        if (feature.id.includes('-')) {
          const category = feature.id.split('-')[0];
          categories[category] = true; // Default to expanded
        }
      });
      
      setExpandedCategories(categories);
    }
  }, [availableFeatures, selectedFeatures, groupByCategory]);

  // Highlight search terms in text
  const highlightText = (text, searchTerm) => {
    if (!searchTerm.trim()) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return (
      <>
        {parts.map((part, i) => regex.test(part) ? 
          <mark key={i} className="bg-yellow-200 px-0.5 rounded">{part}</mark> : part
        )}
      </>
    );
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-3 sm:p-6">
      <div className="w-full mx-auto">
        {/* Modern header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border-l-4 border-blue-500">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-6 lg:mb-0">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                Role Management
              </h1>
              <p className="text-gray-600 text-sm">Create and manage user roles with dashboard access permissions</p>
            </div>
            <button
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
              onClick={() => openModal()}
            >
              <FaPlus className="text-lg" /> Create New Role
            </button>
          </div>
        </div>

        {/* Loading states */}
        {loading && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading roles...</p>
          </div>
        )}

        {!loading && roles.length === 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaPlus className="text-gray-400 text-2xl" />
            </div>
            <p className="text-gray-500 text-lg font-medium mb-2">No roles found</p>
            <p className="text-gray-400 text-sm">Create your first role to get started</p>
          </div>
        )}

        {/* Modern roles table */}
        {!loading && roles.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                Roles List
                <span className="text-sm font-normal text-gray-500">({roles.length} total)</span>
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Role Name</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Dashboard Access</th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {roles.map((role) => (
                    <tr key={role._id} className="hover:bg-gray-50 transition-colors group">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                            {role.postName.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm font-medium text-gray-900">{role.postName}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          {role.dashboardAccess.slice(0, 3).map((feature) => (
                            <span key={feature} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {feature}
                            </span>
                          ))}
                          {role.dashboardAccess.length > 3 && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              +{role.dashboardAccess.length - 3} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openModal(role)}
                            className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-lg transition-all shadow-md hover:shadow-lg group-hover:scale-105"
                            title="Edit Role"
                          >
                            <FaPen size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteRole(role._id)}
                            className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-all shadow-md hover:shadow-lg group-hover:scale-105"
                            title="Delete Role"
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {/* Enhanced Modal with modern design */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex justify-center items-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">
                    {isEditing ? 'Edit Role' : 'Create New Role'}
                  </h2>
                  <p className="text-blue-100 text-sm mt-1">
                    {isEditing ? 'Update role permissions and access' : 'Define role permissions and dashboard access'}
                  </p>
                </div>
                <button 
                  onClick={() => setShowKeyboardGuide(!showKeyboardGuide)}
                  className="text-blue-100 hover:text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-all flex items-center gap-2"
                  title="Keyboard shortcuts"
                >
                  <FaKeyboard size={20} />
                  <span className="text-sm">Keyboard Shortcuts</span>
                </button>
              </div>
              
              {/* Keyboard shortcuts guide */}
              {showKeyboardGuide && (
                <div className="mt-4 p-4 bg-white bg-opacity-10 rounded-xl backdrop-blur-sm border border-white border-opacity-20">
                  <h3 className="font-semibold mb-3 text-blue-100">Keyboard Shortcuts</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <kbd className="px-2 py-1 bg-white bg-opacity-20 text-blue-100 rounded text-xs">↑/↓</kbd>
                        <span className="text-blue-100">Navigate items</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <kbd className="px-2 py-1 bg-white bg-opacity-20 text-blue-100 rounded text-xs">Enter</kbd>
                        <span className="text-blue-100">Move selected items</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <kbd className="px-2 py-1 bg-white bg-opacity-20 text-blue-100 rounded text-xs">Space</kbd>
                        <span className="text-blue-100">Select/deselect item</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <kbd className="px-2 py-1 bg-white bg-opacity-20 text-blue-100 rounded text-xs">Ctrl+A</kbd>
                        <span className="text-blue-100">Select all visible</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <kbd className="px-2 py-1 bg-white bg-opacity-20 text-blue-100 rounded text-xs">/</kbd>
                        <span className="text-blue-100">Focus search</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <kbd className="px-2 py-1 bg-white bg-opacity-20 text-blue-100 rounded text-xs">Esc</kbd>
                        <span className="text-blue-100">Clear selection</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <form onSubmit={handleSubmit}>
                {/* Role Name Input */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Role Name *
                  </label>
                  <input
                    type="text"
                    name="postName"
                    value={formData.postName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Enter role name (e.g., Manager, Developer, Admin)"
                    required
                  />
                </div>
                
                {/* Dashboard Access Management */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Dashboard Access Permissions
                      </label>
                      <p className="text-sm text-gray-500">
                        Manage which features this role can access. Dashboard access is always included.
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-sm text-gray-500">
                        Available: <span className="font-semibold text-blue-600">{filteredAvailableFeatures.length}</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        Selected: <span className="font-semibold text-green-600">{filteredSelectedFeatures.length}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Available Features Panel */}
                    <div className="lg:col-span-5">
                      <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                            Available Features
                            <span className="text-sm font-normal text-gray-500">({filteredAvailableFeatures.length})</span>
                          </h3>
                          <div className="flex items-center gap-2">
                            {filteredAvailableFeatures.length > 0 && (
                              <>
                                <button
                                  type="button"
                                  onClick={selectAllAvailable}
                                  className="text-blue-600 hover:bg-blue-50 p-1 rounded-md transition-colors"
                                  title="Select all visible"
                                >
                                  <FaCheckSquare size={16} />
                                </button>
                                <button
                                  type="button"
                                  onClick={deselectAllAvailable}
                                  className="text-blue-600 hover:bg-blue-50 p-1 rounded-md transition-colors"
                                  title="Deselect all"
                                >
                                  <FaSquare size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Search Box */}
                        <div className="mb-3 relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaSearch className="text-gray-400" size={16} />
                          </div>
                          <input
                            type="text"
                            ref={availableSearchRef}
                            value={availableSearch}
                            onChange={(e) => setAvailableSearch(e.target.value)}
                            placeholder="Search available features..."
                            className="pl-10 pr-10 py-2.5 w-full border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          />
                          {availableSearch && (
                            <button
                              type="button"
                              onClick={() => setAvailableSearch('')}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                              <span className="text-xl">×</span>
                            </button>
                          )}
                        </div>
                        
                        {/* Features List */}
                        <div 
                          ref={availableListRef}
                          tabIndex={0}
                          onKeyDown={(e) => handleKeyDown(e, 'available')}
                          onFocus={() => setFocusedList('available')}
                          className={`min-h-[350px] max-h-[450px] overflow-y-auto rounded-lg border-2 transition-all ${
                            focusedList === 'available' 
                              ? 'border-blue-400 bg-blue-50' 
                              : 'border-gray-200 bg-white'
                          }`}
                        >
                          {filteredAvailableFeatures.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full py-12">
                              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                <FaSearch className="text-gray-400 text-xl" />
                              </div>
                              <p className="text-gray-500 text-center">
                                {availableSearch ? 'No matching features found' : 'All features are already selected'}
                              </p>
                            </div>
                          ) : (
                            <div className="p-3 space-y-2">
                              {filteredAvailableFeatures.map((item) => (
                                <div 
                                  key={item.id}
                                  onClick={() => handleSelectAvailable(item.id)}
                                  onDoubleClick={() => handleDoubleClickAvailable(item.id)}
                                  className={`p-3 rounded-lg cursor-pointer border-2 transition-all duration-200 hover:shadow-md ${
                                    selectedAvailableItems.includes(item.id) 
                                      ? 'bg-blue-100 border-blue-300 shadow-md transform scale-105' 
                                      : 'bg-white border-gray-200 hover:bg-gray-50 hover:shadow-sm'
                                  }`}
                                  title="Click to select, double-click to add immediately"
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-800">
                                      {highlightText(item.content, availableSearch)}
                                    </span>
                                    {selectedAvailableItems.includes(item.id) && (
                                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                        <span className="text-white text-xs">✓</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Control Buttons */}
                    <div className="lg:col-span-2 flex lg:flex-col justify-center items-center gap-3">
                      <div className="flex lg:flex-col gap-3">
                        {/* Add All Button */}
                        {filteredAvailableFeatures.length > 0 && (
                          <button
                            type="button"
                            onClick={moveAllToSelected}
                            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center gap-2"
                            title="Add all visible features"
                          >
                            <FaArrowRight />
                            <span className="text-sm font-medium">All</span>
                          </button>
                        )}
                        
                        {/* Add Selected Button */}
                        <button
                          type="button"
                          onClick={moveToSelected}
                          disabled={selectedAvailableItems.length === 0}
                          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
                          title="Add selected features"
                        >
                          <FaArrowRight />
                          <span className="text-sm font-medium">Add</span>
                        </button>
                        
                        {/* Remove Selected Button */}
                        <button
                          type="button"
                          onClick={moveToAvailable}
                          disabled={selectedSelectedItems.length === 0}
                          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-4 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
                          title="Remove selected features"
                        >
                          <FaArrowLeft />
                          <span className="text-sm font-medium">Remove</span>
                        </button>
                        
                        {/* Remove All Button */}
                        {filteredSelectedFeatures.length > 1 && filteredSelectedFeatures.some(f => f.id !== 'dashboard') && (
                          <button
                            type="button"
                            onClick={moveAllToAvailable}
                            className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center gap-2"
                            title="Remove all features (except Dashboard)"
                          >
                            <FaArrowLeft />
                            <span className="text-sm font-medium">All</span>
                          </button>
                        )}
                      </div>
                      
                      {/* Divider */}
                      <div className="w-full h-px bg-gray-300 lg:w-px lg:h-8 my-2"></div>
                      
                      {/* Reorder Buttons */}
                      <div className="flex lg:flex-col gap-2">
                        {selectedSelectedItems.length > 0 && (
                          <div className="text-xs text-center text-gray-600 mb-2 lg:mb-0 lg:text-center">
                            {selectedSelectedItems.filter(id => id !== 'dashboard').length} selected
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={moveUp}
                          disabled={selectedSelectedItems.length === 0 || 
                                   selectedSelectedItems.every(id => id === 'dashboard') ||
                                   selectedSelectedItems.filter(id => id !== 'dashboard').every(id => {
                                     const index = selectedFeatures.findIndex(item => item.id === id);
                                     return index <= 0;
                                   })}
                          className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white p-2 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed disabled:transform-none relative"
                          title={`Move ${selectedSelectedItems.filter(id => id !== 'dashboard').length} item${selectedSelectedItems.filter(id => id !== 'dashboard').length !== 1 ? 's' : ''} up in order`}
                        >
                          <FaArrowUp />
                          {selectedSelectedItems.filter(id => id !== 'dashboard').length > 1 && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                              {selectedSelectedItems.filter(id => id !== 'dashboard').length}
                            </span>
                          )}
                        </button>
                        
                        <button
                          type="button"
                          onClick={moveDown}
                          disabled={selectedSelectedItems.length === 0 || 
                                   selectedSelectedItems.every(id => id === 'dashboard') ||
                                   selectedSelectedItems.filter(id => id !== 'dashboard').every(id => {
                                     const index = selectedFeatures.findIndex(item => item.id === id);
                                     return index >= selectedFeatures.length - 1 || index === -1;
                                   })}
                          className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white p-2 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed disabled:transform-none relative"
                          title={`Move ${selectedSelectedItems.filter(id => id !== 'dashboard').length} item${selectedSelectedItems.filter(id => id !== 'dashboard').length !== 1 ? 's' : ''} down in order`}
                        >
                          <FaArrowDown />
                          {selectedSelectedItems.filter(id => id !== 'dashboard').length > 1 && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                              {selectedSelectedItems.filter(id => id !== 'dashboard').length}
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Selected Features Panel */}
                    <div className="lg:col-span-5">
                      <div className="bg-green-50 rounded-xl p-4 border-2 border-green-200">
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            Selected Features
                            <span className="text-sm font-normal text-gray-500">({filteredSelectedFeatures.length})</span>
                          </h3>
                          <div className="flex items-center gap-2">
                            {filteredSelectedFeatures.some(item => item.id !== 'dashboard') && (
                              <>
                                <button
                                  type="button"
                                  onClick={selectAllSelected}
                                  className="text-green-600 hover:bg-green-100 p-1 rounded-md transition-colors"
                                  title="Select all visible (except Dashboard)"
                                >
                                  <FaCheckSquare size={16} />
                                </button>
                                <button
                                  type="button"
                                  onClick={deselectAllSelected}
                                  className="text-green-600 hover:bg-green-100 p-1 rounded-md transition-colors"
                                  title="Deselect all"
                                >
                                  <FaSquare size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Search Box */}
                        <div className="mb-3 relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaSearch className="text-gray-400" size={16} />
                          </div>
                          <input
                            type="text"
                            ref={selectedSearchRef}
                            value={selectedSearch}
                            onChange={(e) => setSelectedSearch(e.target.value)}
                            placeholder="Search selected features..."
                            className="pl-10 pr-10 py-2.5 w-full border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                          />
                          {selectedSearch && (
                            <button
                              type="button"
                              onClick={() => setSelectedSearch('')}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                              <span className="text-xl">×</span>
                            </button>
                          )}
                        </div>
                        
                        {/* Features List */}
                        <div 
                          ref={selectedListRef}
                          tabIndex={0}
                          onKeyDown={(e) => handleKeyDown(e, 'selected')}
                          onFocus={() => setFocusedList('selected')}
                          className={`min-h-[350px] max-h-[450px] overflow-y-auto rounded-lg border-2 transition-all ${
                            focusedList === 'selected' 
                              ? 'border-green-400 bg-green-100' 
                              : 'border-green-200 bg-white'
                          }`}
                        >
                          {filteredSelectedFeatures.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full py-12">
                              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                <FaSearch className="text-gray-400 text-xl" />
                              </div>
                              <p className="text-gray-500 text-center">
                                {selectedSearch ? 'No matching features found' : 'No features selected'}
                              </p>
                            </div>
                          ) : (
                            <div className="p-3 space-y-2">
                              {filteredSelectedFeatures.map((item, index) => (
                                <div 
                                  key={item.id}
                                  onClick={() => handleSelectSelected(item.id)}
                                  onDoubleClick={() => handleDoubleClickSelected(item.id)}
                                  className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                                    item.id === 'dashboard'
                                      ? 'bg-gradient-to-r from-blue-100 to-blue-200 border-blue-300 cursor-not-allowed' 
                                      : selectedSelectedItems.includes(item.id)
                                        ? 'bg-green-100 border-green-300 shadow-md transform scale-105 cursor-pointer' 
                                        : 'bg-white border-green-200 hover:bg-green-50 hover:shadow-sm cursor-pointer'
                                  }`}
                                  title={item.id === 'dashboard' ? 'Dashboard access is required' : 'Click to select, double-click to remove immediately'}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-bold text-gray-600">
                                        {index + 1}
                                      </div>
                                      <span className="text-sm font-medium text-gray-800">
                                        {highlightText(item.content, selectedSearch)}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {item.id === 'dashboard' && (
                                        <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                                          Required
                                        </span>
                                      )}
                                      {selectedSelectedItems.includes(item.id) && item.id !== 'dashboard' && (
                                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                                          <span className="text-white text-xs">✓</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 hover:shadow-md transition-all font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </div>
                    ) : (
                      isEditing ? 'Update Role' : 'Create Role'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default RoleManagement;